import { makeAutoObservable, runInAction } from 'mobx'
import { getSSOConfigs, createSSOConfig as createSSOConfigAPI, updateSSOConfig as updateSSOConfigAPI, SSOConfig, deleteSSOConfigs as deleteSSOConfigsAPI, testSSOConnection, TestConnectionResult } from 'API/sso-config'
import { RootStore } from 'stores'

// SSO Configuration Store

export default class SsoConfigStore {
  ssoConfigs: SSOConfig[] = []
  isLoading: boolean = false
  rootStore: RootStore

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  fetchSSOConfigs = async () => {
    this.isLoading = true
    try {
      const configs = await getSSOConfigs()
      console.log('📊 SSO Store - Fetched configs:', configs)
      runInAction(() => {
        this.ssoConfigs = configs
        this.isLoading = false
      })
    } catch (error) {
      console.error('❌ SSO Store - Error fetching configs:', error)
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  createSSOConfig = async (ssoConfig: Omit<SSOConfig, 'id'>) => {
    this.isLoading = true
    try {
      const newConfig = await createSSOConfigAPI([ssoConfig])
      runInAction(() => {
        this.ssoConfigs.push(newConfig[0])
        this.isLoading = false
      })
      return newConfig[0]
    } catch (error) {
      console.error(error)
      runInAction(() => {
        this.isLoading = false
      })
      return undefined
    }
  }

  updateSSOConfig = async (id: number, ssoConfig: Partial<SSOConfig>) => {
    this.isLoading = true
    try {
      await updateSSOConfigAPI(id, ssoConfig)
      runInAction(() => {
        const index = this.ssoConfigs.findIndex(c => c.id === id)
        if (index > -1) {
          this.ssoConfigs[index] = { ...this.ssoConfigs[index], ...ssoConfig }
        }
        this.isLoading = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  deleteSSOConfigs = async () => {
    this.isLoading = true
    try {
      await deleteSSOConfigsAPI()
      runInAction(() => {
        this.ssoConfigs = []
        this.isLoading = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  testConnection = async (id: number): Promise<TestConnectionResult> => {
    try {
      const result = await testSSOConnection(id)
      return result
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  // Batch save: create/update multiple SSO configs, then update organization, finally refetch
  saveAll = async (
    payload: {
      ssoConfigs: Partial<SSOConfig>[]
      organizationUpdate: { ssoUserProvisioningPolicy: 'auto-create' | 'existing-only'; name: string; subdomain: string }
    }
  ): Promise<void> => {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    this.isLoading = true
    try {
      // Create/Update SSO configs sequentially to keep order deterministic
      for (const config of payload.ssoConfigs) {
        if (config.id) {
          await updateSSOConfigAPI(config.id, config)
          runInAction(() => {
            const index = this.ssoConfigs.findIndex(c => c.id === config.id)
            if (index > -1) {
              this.ssoConfigs[index] = { ...this.ssoConfigs[index], ...config }
            }
          })
        } else {
          const created = await createSSOConfigAPI([
            {
              ...(config as Omit<SSOConfig, 'id'>),
              organizationId
            }
          ])
          runInAction(() => {
            if (created && created[0]) {
              this.ssoConfigs.push(created[0])
            }
          })
        }
      }

      // Update organization data
      await this.rootStore.organizationStore.updateOrganization(organizationId, payload.organizationUpdate)

      // Refetch latest SSO configs from server to ensure consistency
      await this.fetchSSOConfigs()

      runInAction(() => {
        this.isLoading = false
      })
    } catch (error) {
      console.error('❌ SSO Store - saveAll error:', error)
      runInAction(() => {
        this.isLoading = false
      })
      throw error
    }
  }
}